// survey/handlers/submitCompletedSurvey.js
import { SG_POST_BRIEF_RESPONSE, RESET_VALIDATION_TOKEN, CLEAR_ALL } from "constants/actions";
import { deviceDetect } from "react-device-detect";
import { questionTypeQuestion, introductionTypeQuestion, categoryTypeQuestion } from "../constants/questionTypes";
import { NOT_ALL_QUESTIONS_ANSWERED, SURVEY_SUBMISSION_SUCCESS, ATTESTATION_MESSAGE } from "../constants/constants";

// tiny helpers
const pick = (obj, keys) =>
  Object.fromEntries(keys.filter(k => obj?.[k] !== undefined).map(k => [k, obj[k]]));

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

// Likert (questions) -> compact shape
export const compactLikert = (qs = []) =>
  qs.map(q => ({
    ...pick(q, ["ques_id", "factor", "response", "ques_order", "question_text"]),
  }));

// Intro/attestation steps from sequence map
const compactAttestations = (seqMap = {}) =>
  Object.values(seqMap || {})
    .filter(s => s?.quesType === introductionTypeQuestion && s?.id === 4)
    .filter(s => s?.buttonText)
    .map(s => ({
      attestation_prompt: ATTESTATION_MESSAGE,
      button_text: s?.buttonText,
      response: s?.response?.response === true,
    }));

// Extract numeric Likert responses from sequence map
function extractLikertFromSequenceMap(sequenceMap = {}) {
  const out = [];
  for (const [key, step] of Object.entries(sequenceMap)) {
    if (!step || step?.quesType !== questionTypeQuestion || step?.skip) continue;

    const raw = step?.response?.response ?? step?.response;
    if (typeof raw !== "number" || !Number.isFinite(raw)) continue;

    const questionText = step?.question_text ?? "";

    out.push({
      // 🔑 keep the step id so completeness checks work
      id: step?.id ?? (typeof key === "string" ? Number(key) : key),

      // everything your payload needs
      factor: step?.factor ?? 0,
      response: raw,
      ques_order: step?.ques_order ?? step?.sort_order ?? step?.sno ?? undefined,
      ques_id: step?.ques_id ?? undefined,
      question_text: questionText,

      // keep this in case other code relies on it
      ques_sort_order:
        step?.ques_sort_order ??
        step?.sort_order ??
        (typeof key === "string" ? Number(key) || undefined : undefined),
    });
  }
  return out;
}

// Extract outcome questions [{ q, s, question, response }] from sequence map
function extractModularOutcomesFromSequenceMap(sequenceMap = {}) {
  const results = [];
  for (const [, step] of Object.entries(sequenceMap)) {
    if (step?.quesType !== "modularOutcomeTypeQuestion" || step?.skip) continue;
    const val = step?.response?.response ?? step?.response;
    if (typeof val !== "number" || !Number.isFinite(val)) continue;
    const qObj = step?.question || {};
    results.push({
      q: String(step?.q ?? qObj?.q ?? ""),
      s: String(step?.s ?? qObj?.s ?? ""),
      name: step?.name ?? "",
      question: qObj?.question ?? "",
      response: val,
    });
  }
  return results;
}

// Extract categories from sequence map -> ALWAYS include category steps,
// even when skip === true (never_ask / ask_if_not_found). Also avoid the
// non-category completion screen by requiring a category_question_id.
// Extract categories from the sequence map, ALWAYS include category steps (skip true/false),
// keep the display name and selected option label when available,
// and ignore the category "thank you" page (no category_question_id).
function extractCategoriesFromSequenceMap(sequenceMap = {}) {
  const out = [];
  for (const [, step] of Object.entries(sequenceMap)) {
    if (step?.quesType !== categoryTypeQuestion) continue;

    const catId = step?.category_question_id;
    if (!catId) continue; // avoid the category completion page

    const r = step?.response;
    const valueId = (r && typeof r === "object") ? r.response : undefined;
    const valueName = (r && typeof r === "object") ? r.name : undefined;

    const item = {
      name: typeof step?.name === "string" ? step.name : undefined, 
      id: catId,
      response: valueId ?? null,                  // null for never_ask with no value
      priority: step?.priority ?? r?.priority,    // keep priority if present
    };

    // prune undefined keys, but keep null response explicitly
    Object.keys(item).forEach(k => item[k] === undefined && delete item[k]);

    // optionally include the selected option label if you want it in your payload
    if (typeof valueName === "string") {
      item.response_name = valueName;
    }

    out.push(item);
  }
  return out;
}


// Extract feedback entries from sequence map
// Keeps only: survey_q_id, question, factor, response, feedback, prompt_feedback, prompt_question
function extractFeedbackFromSequenceMap(sequenceMap = {}) {
  const keepKeys = [
    "survey_q_id",
    "question",
    "factor",
    "response",
    "question_text",
    "feedback",
    "question_type",
    "followup_from_q_id",
  ];

  const isFeedbackType = (t) => t === "feedback" || t === "aiFollowUp";

  const out = [];
  for (const [, step] of Object.entries(sequenceMap)) {
    if (!isFeedbackType(step?.quesType) || step?.skip) continue;
    const resp = step?.response;
    if (!isObj(resp)) continue;

    const normalized = {
      survey_q_id: step?.id ?? resp.survey_q_id ?? resp.id ?? null,
      question_type: step.quesType,
      factor: resp.factor,
      response: resp.response,
      question_text: resp.question_text,
      feedback: resp.feedback,
    };

    if (step?.quesType === "aiFollowUp") {
      normalized.followup_from_q_id = step?.quesRefId ?? resp.survey_q_id ?? resp.id ?? null;
    }

    const pruned = pick(normalized, keepKeys);
    if (Object.keys(pruned).length) out.push(pruned);
  }
  return out;
}

// main
export function submitCompletedSurvey({
  dispatch,
  briefResponse,
  totalQuestions,
  questionSequenceMap,
}) {
  const deviceDetected = deviceDetect();

  // Build all sections strictly from the sequence map
  const seqLikertArray = extractLikertFromSequenceMap(questionSequenceMap);
  const seqModularArray = extractModularOutcomesFromSequenceMap(questionSequenceMap);
  const seqCategories = extractCategoriesFromSequenceMap(questionSequenceMap);
  const seqFeedback = extractFeedbackFromSequenceMap(questionSequenceMap);
  const attestations = compactAttestations(questionSequenceMap);

  // Required-likert validation using totalQuestions.sequence
  const seqDef = totalQuestions?.sequence || {};
  const requiredQuestionIds = Object.values(seqDef)
    .filter(s => s?.quesType === questionTypeQuestion && !s?.skip)
    .map(s => String(s.id));

  const answeredIds = (seqLikertArray || [])
    .map(item => item?.id)
    .filter(id => id !== undefined && id !== null)
    .map(id => String(id));

  const missing = requiredQuestionIds.filter(id => !answeredIds.includes(id));

  if (missing.length > 0) {
    console.warn("Cannot submit: Not all questions answered");
    console.warn("Missing answers for steps ->", missing, "\nAnswered IDs:", answeredIds);
    return NOT_ALL_QUESTIONS_ANSWERED;
  }

  const minimalPayload = {
    survey_token: briefResponse?.survey_token,
    debrief_schedule: briefResponse?.debrief_schedule,
    employee: briefResponse?.employee?.id || briefResponse?.employee,
    response: {
      version: 3,
      feedback: seqFeedback,
      questions: compactLikert(seqLikertArray),
      categories: seqCategories,
      outcome_questions: seqModularArray,
      attestations,
    },
    device: deviceDetected,
  };

  dispatch({ type: SG_POST_BRIEF_RESPONSE, payload: minimalPayload });
  dispatch({ type: RESET_VALIDATION_TOKEN, payload: null });
  dispatch({ type: CLEAR_ALL, payload: null });
  localStorage.clear();
  return SURVEY_SUBMISSION_SUCCESS;
}
